import React, { useEffect, useState } from "react";
import styles from "./HeaderComponent.module.scss";
import Logo from "../UI/Logo/Logo";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import {
  selectIsAuthenticated,
  selectIsAdmin,
  selectIsPharmacist,
  selectActiveLicense,
  selectIsConsultOnlySession,
} from "../../store/auth-slice";
import HeaderMenu from "./HeaderMenu/HeaderMenu";
import PharmacistInfo from "./PharmacistInfo/PharmacistInfo";
import { useLocation, useNavigate } from "react-router-dom";
import { pingActiveStatus } from "../../store/auth-actions";

const HeaderComponent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const marketplaceInfo = useSelector(
    (state: RootState) => state.auth.marketplaceInfo
  );
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isAdmin = useSelector(selectIsAdmin);
  const isPharmacist = useSelector(selectIsPharmacist);
  const activeLicense = useSelector(selectActiveLicense);
  const isConsultOnly = useSelector(selectIsConsultOnlySession);

  // Derived from Redux selectors - regular marketplace user.
  //
  // ITAM-2622: `isCnCUser` is LOAD-BEARING beyond the menu — it is also the
  // arming guard for the retail keep-alive interval below. Widening it so the
  // consult-only pharmacist gets a menu would arm a retail `takesOrders=1`
  // ping for a session that has no retail pharmacy. So the menu gets its own
  // predicate instead, AND the retail predicate excludes the consult-only
  // session outright: leaving it out would make the guard rest on the
  // conjunction's second term (a consult-only session happening to have a null
  // `marketplaceInfo`), which is a coincidence rather than a design. A
  // dual-role session is not consult-only, so it keeps the retail keep-alive.
  const isCnCUser =
    isAuthenticated && !isAdmin && !isPharmacist && !isConsultOnly;
  const showHeaderMenu = isCnCUser || isConsultOnly;
  const [RFZOMenuOpen, setRFZOMenuOpen] = useState(false);

  // Status monitoring - redirect to profile when inactive
  useEffect(() => {
    if (marketplaceInfo && marketplaceInfo.takesOrders !== 1) {
      navigate("/profile");
    }
  }, [marketplaceInfo, navigate]);

  // Keep-alive ping for marketplace users - moved from OrderProcessingPage
  useEffect(() => {
    if (isCnCUser && marketplaceInfo && marketplaceInfo.takesOrders === 1) {
      const interval = setInterval(() => {
        dispatch(
          pingActiveStatus(
            marketplaceInfo.branchMarketplaceId,
            marketplaceInfo.marketplaceId
          ) as any
        );
      }, 120000); // 2 minutes

      return () => clearInterval(interval);
    }
  }, [dispatch, isCnCUser, marketplaceInfo]);

  // User type determination now handled by computed value above

  const location = useLocation();
  const linkMapping = {
    // For the consult-only session /profile is only a logout surface, and its
    // menu entry is named accordingly — the pill must use the same name.
    profile: isConsultOnly ? "Odjavi se" : "Profil",
    "order-processing": "Pregled rezervacija",
    "admin-marketplace": "Pregled apoteka",
    transactions: "Transakcije",
    statistics: "Statistika",
    "rfzo-reminders": "Podsetnici",
    patients: "Omiljena apoteka",
    "pharmacist-profile": "Profil savetnika",
  };
  const currentLink: string =
    location.pathname.replace("/", "") ||
    (isConsultOnly ? "pharmacist-profile" : "order-processing");
  const textToShow: any =
    linkMapping[currentLink as keyof typeof linkMapping] || "";

  const toggleRFZOMenu = () => {
    setRFZOMenuOpen(!RFZOMenuOpen);
  };

  return (
    <div className={styles.Header}>
      <Logo />
      <div className={styles.ActionsWrapper}>
        {/* Show pharmacist info for CnC users when a pharmacist is active */}
        {isCnCUser && activeLicense && <PharmacistInfo />}

        {showHeaderMenu && (
          <div
            onClick={toggleRFZOMenu}
            className={styles.RFZOMenu}
            style={{
              backgroundColor: RFZOMenuOpen ? "#DEDFE1" : "inherit",
            }}
          >
            <span>{textToShow}</span>
            {RFZOMenuOpen && <HeaderMenu />}
          </div>
        )}
        {/* UserIcon removed - functionality now in HeaderMenu */}
      </div>
    </div>
  );
};

export default HeaderComponent;
